/* ------------------------------------------------------------------
   Zenzorama sok v2 - predictive search

   <zenz-predictive-search> pakker inn et vanlig sokeskjema og legger en
   rullgardin under feltet. All datahenting gaar mot Shopifys native
   Search Suggest-endepunkt (/search/suggest) med Section Rendering,
   slik at markupen kommer fra sections/zenz-predictive-search.liquid.

   - starter etter 2 tegn, 250 ms debounce
   - avbryter utdaterte forespoersler (AbortController + sekvensvakt)
   - combobox/listbox-semantikk med aria-activedescendant
   - piltaster, Enter, Escape, klikk utenfor
   - vanlig Enter-soek og soekeikonet fungerer som for
   - tomt eller for kort soek (under 2 tegn) sendes aldri; teksten trimmes
     og doble mellomrom slaas sammen foer innsending (2026-10-10)
   ------------------------------------------------------------------ */

(function () {
  'use strict';

  if (typeof customElements === 'undefined' || customElements.get('zenz-predictive-search')) return;

  var MIN_CHARS = 2;
  var FOR_KORT = 'Skriv minst to tegn for å søke.';
  var DEBOUNCE_MS = 250;
  var RESOURCE_TYPES = 'product,page,article,collection';
  var RESOURCE_LIMIT = 10;
  var instances = 0;

  var SKELETON =
    '<div class="zsx-skeleton" aria-hidden="true">' +
    '<div class="zsx-skeleton__row"><span class="zsx-skeleton__box"></span><span><span class="zsx-skeleton__line"></span><span class="zsx-skeleton__line"></span></span></div>'.repeat(3) +
    '</div>';

  class ZenzPredictiveSearch extends HTMLElement {
    constructor() {
      super();
      instances += 1;
      this.uid = 'zsx-' + instances;
      this.cache = {};
      this.controller = null;
      this.timer = null;
      this.requestId = 0;
      this.options = [];
      this.activeIndex = -1;
      this.lastQuery = '';
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('[data-zsx-input]');
      this.panel = this.querySelector('[data-zsx-panel]');
      this.results = this.querySelector('[data-zsx-results]');
      this.status = this.querySelector('[data-zsx-status]');

      if (!this.form || !this.input || !this.panel || !this.results) return;

      this.suggestUrl = this.getAttribute('data-suggest-url') || '/search/suggest';
      this.sectionId = this.getAttribute('data-section-id') || 'zenz-predictive-search';

      this.results.id = this.uid + '-listbox';
      this.results.setAttribute('role', 'listbox');
      if (!this.results.getAttribute('aria-label')) {
        this.results.setAttribute('aria-label', 'Søkeforslag');
      }

      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-haspopup', 'listbox');
      this.input.setAttribute('aria-controls', this.results.id);
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('autocomplete', 'off');
      this.input.setAttribute('autocorrect', 'off');
      this.input.setAttribute('autocapitalize', 'off');
      this.input.setAttribute('spellcheck', 'false');
      /* Grunnvern uten JavaScript-logikken under: tomt felt stoppes av nettleseren. */
      this.input.setAttribute('required', '');
      this.input.setAttribute('minlength', String(MIN_CHARS));

      this.onInput = this.onInput.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onFocus = this.onFocus.bind(this);
      this.onDocumentPointer = this.onDocumentPointer.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onSubmit = this.onSubmit.bind(this);

      this.onInvalid = this.onInvalid.bind(this);
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('invalid', this.onInvalid);
      this.input.addEventListener('keydown', this.onKeydown);
      this.input.addEventListener('focus', this.onFocus);
      this.form.addEventListener('submit', this.onSubmit);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('pointerdown', this.onDocumentPointer);

      this.setAttribute('data-open', 'false');
      this.setAttribute('data-loading', 'false');
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this.onDocumentPointer);
      if (this.controller) this.controller.abort();
      clearTimeout(this.timer);
    }

    /* -------------------------------------------------- input handling */

    onInput() {
      this.input.setCustomValidity('');
      var value = this.input.value.trim();
      clearTimeout(this.timer);

      if (value.length < MIN_CHARS) {
        this.abort();
        this.lastQuery = '';
        this.close();
        this.results.innerHTML = '';
        this.setAttribute('data-loading', 'false');
        return;
      }

      if (value === this.lastQuery) {
        // Samme soekeord som sist: ikke hent paa nytt, men aapne igjen hvis
        // kunden hadde lukket rullgardinen (Escape eller klikk utenfor).
        if (!this.isOpen() && this.options.length) this.open();
        return;
      }

      this.timer = setTimeout(function () {
        this.search(value);
      }.bind(this), DEBOUNCE_MS);
    }

    onFocus() {
      var value = this.input.value.trim();
      if (value.length >= MIN_CHARS && this.results.innerHTML.trim() !== '' && this.options.length) {
        this.open();
      }
    }

    onSubmit(event) {
      clearTimeout(this.timer);
      this.abort();
      this.close();

      // Trim og slaa sammen mellomrom; under MIN_CHARS sendes ingenting.
      var value = this.input.value.replace(/\s+/g, ' ').trim();
      if (value.length < MIN_CHARS) {
        event.preventDefault();
        this.input.value = value;
        this.input.setCustomValidity(FOR_KORT);
        this.input.reportValidity();
        this.input.focus();
        this.announce(FOR_KORT);
        return;
      }
      this.input.setCustomValidity('');
      if (value !== this.input.value) this.input.value = value;
    }

    // Nettleserens egen validering (required/minlength) skal si det samme paa norsk.
    onInvalid() {
      this.input.setCustomValidity(FOR_KORT);
    }

    onKeydown(event) {
      var key = event.key;

      if (key === 'Escape') {
        if (this.isOpen()) {
          event.preventDefault();
          event.stopPropagation();
          this.close();
          this.input.focus();
        }
        return;
      }

      if (key === 'ArrowDown' || key === 'ArrowUp') {
        if (!this.isOpen() || !this.options.length) return;
        event.preventDefault();
        this.move(key === 'ArrowDown' ? 1 : -1);
        return;
      }

      if (key === 'Home' && this.isOpen() && this.activeIndex > -1) {
        event.preventDefault();
        this.setActive(0);
        return;
      }

      if (key === 'End' && this.isOpen() && this.activeIndex > -1) {
        event.preventDefault();
        this.setActive(this.options.length - 1);
        return;
      }

      if (key === 'Enter') {
        if (this.isOpen() && this.activeIndex > -1 && this.options[this.activeIndex]) {
          event.preventDefault();
          window.location.href = this.options[this.activeIndex].href;
        }
        return;
      }

      if (key === 'Tab') {
        this.close();
      }
    }

    onFocusOut(event) {
      var next = event.relatedTarget;
      if (next && this.contains(next)) return;
      this.close();
    }

    onDocumentPointer(event) {
      if (this.contains(event.target)) return;
      this.close();
    }

    /* -------------------------------------------------- data */

    abort() {
      if (this.controller) {
        this.controller.abort();
        this.controller = null;
      }
    }

    search(query) {
      var key = query.toLowerCase();
      this.lastQuery = query;

      if (this.cache[key] !== undefined) {
        this.render(this.cache[key], query);
        return;
      }

      this.abort();
      this.controller = typeof AbortController === 'function' ? new AbortController() : null;

      var id = ++this.requestId;
      var self = this;

      this.setAttribute('data-loading', 'true');
      if (!this.isOpen()) {
        this.results.innerHTML = SKELETON;
        this.options = [];
        this.activeIndex = -1;
        this.open();
      }

      var url =
        this.suggestUrl +
        '?q=' + encodeURIComponent(query) +
        '&resources[type]=' + RESOURCE_TYPES +
        '&resources[limit]=' + RESOURCE_LIMIT +
        '&resources[limit_scope]=each' +
        '&section_id=' + encodeURIComponent(this.sectionId);

      fetch(url, this.controller ? { signal: this.controller.signal } : undefined)
        .then(function (response) {
          if (!response.ok) throw new Error('Predictive search: ' + response.status);
          return response.text();
        })
        .then(function (text) {
          if (id !== self.requestId) return; // utdatert svar - forkast
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var section = doc.querySelector('#shopify-section-' + self.sectionId) || doc.querySelector('.zsx-results');
          var html = section ? section.innerHTML : '';
          self.cache[key] = html;
          self.render(html, query);
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          if (id !== self.requestId) return;
          self.setAttribute('data-loading', 'false');
          self.close();
        });
    }

    /* -------------------------------------------------- rendering */

    render(html, query) {
      this.setAttribute('data-loading', 'false');

      if (!html || !html.trim()) {
        this.results.innerHTML = '';
        this.options = [];
        this.activeIndex = -1;
        this.close();
        this.announce('Ingen forslag for ' + query + '.');
        return;
      }

      this.results.innerHTML = html;
      this.collectOptions();

      if (!this.options.length) {
        this.close();
        this.announce('Ingen forslag for ' + query + '.');
        return;
      }

      this.open();
      this.panel.scrollTop = 0;

      var container = this.results.querySelector('[data-zsx-total]');
      var total = container ? parseInt(container.getAttribute('data-zsx-total'), 10) : 0;
      if (!total) {
        this.announce('Ingen treff for ' + query + '. Du kan fortsatt åpne hele søkeresultatsiden.');
      } else {
        this.announce(total + ' forslag for ' + query + '. Bruk pil opp og ned for å bla.');
      }
    }

    collectOptions() {
      this.options = Array.prototype.slice.call(this.results.querySelectorAll('[role="option"]'));
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');

      for (var i = 0; i < this.options.length; i += 1) {
        this.options[i].id = this.uid + '-opt-' + i;
        this.options[i].setAttribute('aria-selected', 'false');
      }
    }

    move(step) {
      var next = this.activeIndex + step;
      if (next < 0) next = this.options.length - 1;
      if (next > this.options.length - 1) next = 0;
      this.setActive(next);
    }

    setActive(index) {
      if (this.activeIndex > -1 && this.options[this.activeIndex]) {
        this.options[this.activeIndex].setAttribute('aria-selected', 'false');
      }

      this.activeIndex = index;
      var option = this.options[index];
      if (!option) {
        this.input.removeAttribute('aria-activedescendant');
        return;
      }

      option.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', option.id);

      var panelTop = this.panel.scrollTop;
      var panelBottom = panelTop + this.panel.clientHeight;
      var optionTop = option.offsetTop;
      var optionBottom = optionTop + option.offsetHeight;

      if (optionTop < panelTop) {
        this.panel.scrollTop = optionTop - 8;
      } else if (optionBottom > panelBottom) {
        this.panel.scrollTop = optionBottom - this.panel.clientHeight + 8;
      }
    }

    /* -------------------------------------------------- open / close */

    isOpen() {
      return this.getAttribute('data-open') === 'true';
    }

    open() {
      if (this.isOpen()) return;
      this.setAttribute('data-open', 'true');
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      if (!this.isOpen()) {
        this.activeIndex = -1;
        return;
      }
      this.setAttribute('data-open', 'false');
      this.setAttribute('data-loading', 'false');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');

      if (this.activeIndex > -1 && this.options[this.activeIndex]) {
        this.options[this.activeIndex].setAttribute('aria-selected', 'false');
      }
      this.activeIndex = -1;
    }

    announce(message) {
      if (this.status) this.status.textContent = message;
    }
  }

  customElements.define('zenz-predictive-search', ZenzPredictiveSearch);
})();
